// src/pages/projects/RHASMPProject.jsx
import React, { useState, useEffect } from "react";
import {
  Shield,
  Users,
  Heart,
  BookOpen,
  Phone,
  Home,
  TrendingUp,
  AlertTriangle,
} from "lucide-react";

const RHASMPProject = () => {
  const [animateIn, setAnimateIn] = useState(false);

  useEffect(() => {
    setAnimateIn(true);
  }, []);

  // (Kept for parity with your pattern; not rendered directly in this layout)
  const projectStats = [
    { number: "30,000", label: "People Reached (Awareness)", icon: Users },
    { number: "10,000", label: "Individuals Screened (HBV/HCV)", icon: Heart },
    { number: "60", label: "Health Workers Trained", icon: Home },
    { number: "$95k", label: "Total Budget", icon: Shield },
  ];

  // Mirrors SGBV "programPillars"
  const programPillars = [
    {
      icon: BookOpen,
      title: "Community Awareness & Mobilization",
      description:
        "Radio talk shows, community dialogues, and IEC materials to grow knowledge on Hepatitis B & C prevention, transmission, testing, and treatment.",
      highlights: [
        "Radio + community dialogues in all subcounties",
        "IEC materials in local languages",
        "Target reach: 30,000 people",
      ],
    },
    {
      icon: Heart,
      title: "Screening & Vaccination",
      description:
        "Mobile outreach clinics offering free HBV/HCV screening and vaccination for eligible, uninfected individuals.",
      highlights: [
        "Target: 10,000 screened (HBV/HCV)",
        "On-site vaccination against HBV",
        "Pre- & post-test counselling",
      ],
    },
    {
      icon: Users,
      title: "Capacity Building",
      description:
        "Training 60 CHWs and health facility staff on Hepatitis prevention, screening algorithms, vaccination schedules, referral, and data tools.",
      highlights: [
        "Standard job aids & registers",
        "Supportive supervision & mentorship",
        "Quarterly review meetings",
      ],
    },
    {
      icon: Shield,
      title: "Linkage to Care & System Strengthening",
      description:
        "Structured referral to treatment centers with active follow-up; collaboration with DHO & MOH to integrate Hepatitis into routine services.",
      highlights: [
        "Referral directory & focal contacts",
        "Linkage tracking for positives",
        "Integration with HIV/immunization outreach",
      ],
    },
  ];

  const impactAreas = [
    {
      title: "Awareness & Prevention",
      description:
        "Communities understand transmission, prevention, and vaccination benefits.",
    },
    {
      title: "Early Diagnosis",
      description:
        "Increased uptake of HBV/HCV screening and timely referrals.",
    },
    {
      title: "Continuity of Care",
      description:
        "Positive cases linked to treatment and monitored for adherence/outcomes.",
    },
  ];

  return (
    <div className="project-page">
      {/* Bootstrap CSS */}
      <link
        href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.0/dist/css/bootstrap.min.css"
        rel="stylesheet"
      />

      {/* Urgent Banner */}
      <section
        className="urgent-banner"
        role="note"
        aria-label="Early screening callout"
      >
        <div className="container">
          <div className="urgent-content">
            <span className="urgent-icon">
              <AlertTriangle size={20} />
            </span>
            <p className="urgent-text">
              <strong>Back last-mile Hepatitis protection.</strong> Fund
              outreach clinics and vaccines so no one learns their status when
              it’s too late.
            </p>
          </div>
        </div>
      </section>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-background"></div>
        <div className="hero-overlay"></div>
        <div className="container position-relative">
          <div className="row align-items-center min-vh-80">
            <div className="col-lg-6">
              <div className="hero-content">
                <div className="hero-badge">Critical Initiative</div>
                <h1 className="hero-title">
                  Hepatitis B Awareness, Screening & Management Project
                </h1>
                <p className="hero-subtitle">RHASMP • RACOHI • 24 Months</p>
                <p className="hero-description">
                  Reducing the burden of Hepatitis B & C in Rakai District
                  through improved awareness, free community screening, HBV
                  vaccination, and strong referral to care and treatment.
                </p>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="hero-image-container">
                <div className="project-image">
                  <img
                    src="/images/projects/rhasmp-hero.jpg"
                    alt="RHASMP Project"
                    className="hero-image"
                    onError={(e) => {
                      e.target.style.display = "none";
                      e.target.nextSibling.style.display = "flex";
                    }}
                  />
                  <div className="image-placeholder">
                    <Shield size={60} />
                    <span>Project Image</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Project Overview */}
      <section className="overview-section">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6">
              <div className="project-image-container">
                <img
                  src="/images/projects/rhasmp-overview.jpg"
                  alt="RHASMP Overview"
                  className="project-overview-image"
                  onError={(e) => {
                    e.target.style.display = "none";
                    e.target.nextSibling.style.display = "flex";
                  }}
                />
                <div className="image-placeholder">
                  <Heart size={60} />
                  <span>Project Overview</span>
                </div>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="overview-content">
                <div className="section-badge">Our Approach</div>
                <h2 className="section-title">
                  Awareness → Screening → Vaccination → Referral
                </h2>
                <p className="section-description">
                  RHASMP responds to low awareness and service access by
                  bringing HBV/HCV education and testing closer to communities,
                  vaccinating eligible people against HBV, strengthening health
                  worker capacity, and ensuring positive cases are linked to
                  care.
                </p>

                <div className="impact-areas">
                  <h4 className="subsection-title">Core Focus Areas</h4>
                  {impactAreas.map((area, index) => (
                    <div key={index} className="impact-item">
                      <h5 className="impact-title">{area.title}</h5>
                      <p className="impact-description">{area.description}</p>
                    </div>
                  ))}
                </div>

                <div className="d-flex flex-wrap gap-2 mt-3">
                  <span className="badge rounded-pill text-bg-warning-subtle">
                    HBV
                  </span>
                  <span className="badge rounded-pill text-bg-warning-subtle">
                    HCV
                  </span>
                  <span className="badge rounded-pill text-bg-warning-subtle">
                    Vaccination
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Program Pillars */}
      <section className="pillars-section">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div
                className={`section-header text-center ${
                  animateIn ? "animate-in" : ""
                }`}
              >
                <div className="section-badge">Program Pillars</div>
                <h2 className="section-title">Four Pillars of Impact</h2>
                <p className="section-description">
                  An integrated package—from community knowledge to vaccination
                  and referral—that lowers new infections and complications from
                  chronic Hepatitis.
                </p>
              </div>
            </div>
          </div>

          <div className="row">
            {programPillars.map((pillar, index) => (
              <div key={index} className="col-lg-6 mb-4">
                <div className="pillar-card">
                  <div className="pillar-icon">
                    <pillar.icon size={32} />
                  </div>
                  <div className="pillar-content">
                    <h3 className="pillar-title">{pillar.title}</h3>
                    <p className="pillar-description">{pillar.description}</p>
                    <ul className="pillar-highlights">
                      {pillar.highlights.map((highlight, idx) => (
                        <li key={idx}>
                          <TrendingUp size={16} />
                          {highlight}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Emergency / Service Info */}
      <section className="emergency-section">
        <div className="container">
          {/* Partners */}
          <div className="row mt-4">
            <div className="col-12">
              <div className="pillar-card">
                <h4 className="mb-3">Implementation Partners</h4>
                <ul className="pillar-highlights">
                  <li>
                    <Home size={16} /> Rakai District Health Office
                    (coordination & oversight)
                  </li>
                  <li>
                    <Home size={16} /> Uganda Ministry of Health (vaccines &
                    test kits)
                  </li>
                  <li>
                    <Home size={16} /> Uganda Blood Transfusion Services (blood
                    safety & awareness)
                  </li>
                  <li>
                    <Home size={16} /> Local CBOs, Religious & Cultural Leaders
                    (mobilization)
                  </li>
                  <li>
                    <Home size={16} /> Health Facilities (testing, treatment &
                    follow-up)
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Budget Section (replaces CTA) */}
      <section className="budget-section">
        <div className="container">
          <div className="budget-card">
            <h3 className="budget-title">Estimated Budget Summary (EUR)</h3>
            <p className="budget-subtitle">
              Duration: 24 months • Implementer: RACOHI • Total:{" "}
              <strong>€95,000</strong>
            </p>

            <div className="table-responsive">
              <table className="table budget-table align-middle">
                <thead>
                  <tr>
                    <th scope="col">Budget Category</th>
                    <th scope="col" className="text-end">
                      Amount (EUR)
                    </th>
                    <th scope="col" className="text-end">
                      % of Total
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Community awareness & mobilization</td>
                    <td className="text-end">€20,000</td>
                    <td className="text-end">21.1%</td>
                  </tr>
                  <tr>
                    <td>Screening kits & medical supplies</td>
                    <td className="text-end">€30,000</td>
                    <td className="text-end">31.6%</td>
                  </tr>
                  <tr>
                    <td>Vaccination & referral support</td>
                    <td className="text-end">€15,000</td>
                    <td className="text-end">15.8%</td>
                  </tr>
                  <tr>
                    <td>Training & capacity building</td>
                    <td className="text-end">€10,000</td>
                    <td className="text-end">10.5%</td>
                  </tr>
                  <tr>
                    <td>Monitoring & evaluation</td>
                    <td className="text-end">€10,000</td>
                    <td className="text-end">10.5%</td>
                  </tr>
                  <tr>
                    <td>Coordination & administration</td>
                    <td className="text-end">€10,000</td>
                    <td className="text-end">10.5%</td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr>
                    <th scope="row">Total Project Budget</th>
                    <th className="text-end">€95,000</th>
                    <th className="text-end">100%</th>
                  </tr>
                </tfoot>
              </table>
            </div>

            <div className="budget-bars">
              <div className="bar">
                <span>Awareness & mobilization</span>
                <div className="bar-track">
                  <div className="bar-fill" style={{ width: "21.1%" }} />
                </div>
                <strong>21.1%</strong>
              </div>
              <div className="bar">
                <span>Screening kits & supplies</span>
                <div className="bar-track">
                  <div className="bar-fill" style={{ width: "31.6%" }} />
                </div>
                <strong>31.6%</strong>
              </div>
              <div className="bar">
                <span>Vaccination & referral</span>
                <div className="bar-track">
                  <div className="bar-fill" style={{ width: "15.8%" }} />
                </div>
                <strong>15.8%</strong>
              </div>
              <div className="bar">
                <span>Training & capacity</span>
                <div className="bar-track">
                  <div className="bar-fill" style={{ width: "10.5%" }} />
                </div>
                <strong>10.5%</strong>
              </div>
              <div className="bar">
                <span>Monitoring & evaluation</span>
                <div className="bar-track">
                  <div className="bar-fill" style={{ width: "10.5%" }} />
                </div>
                <strong>10.5%</strong>
              </div>
              <div className="bar">
                <span>Coordination & admin</span>
                <div className="bar-track">
                  <div className="bar-fill" style={{ width: "10.5%" }} />
                </div>
                <strong>10.5%</strong>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Styles */}
      <style jsx>{`
        :root {
          --primary-color: #06b6d4;
          --primary-dark: #0891b2;
          --primary-light: #67e8f9;
          --accent-orange: #f59e0b;
          --text-dark: #1f2937;
          --text-gray: #6b7280;
          --text-light: #9ca3af;
          --white: #ffffff;
          --light-bg: #f8fafc;
          --border-light: rgba(0, 0, 0, 0.06);
          --success-green: #10b981;
          --warning-red: #ef4444;
        }

        /* Urgent Banner */
        .urgent-banner {
          background: #dc2626; /* red */
          color: #fff;
          padding: 10px 0;
        }

        .urgent-content {
          display: flex;
          align-items: center;
          gap: 12px;
          justify-content: center;
          text-align: center;
        }

        .urgent-icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          background: rgba(255, 255, 255, 0.18);
          border-radius: 999px;
          padding: 6px;
        }

        .urgent-text {
          margin: 0;
          font-size: 0.98rem;
          line-height: 1.4;
          font-weight: 500;
        }

        @media (max-width: 768px) {
          .urgent-text {
            font-size: 0.92rem;
          }
        }

        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        body {
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto",
            sans-serif;
          line-height: 1.6;
          color: var(--text-dark);
          padding-top: 80px;
        }

        /* Ensure hero content sits above overlay */
        .hero-background,
        .hero-overlay {
          z-index: 0;
          pointer-events: none;
        }
        .hero-section .container,
        .hero-image-container {
          position: relative;
          z-index: 2;
        }

        /* Hero Section */
        .hero-section {
          position: relative;
          min-height: 85vh;
          display: flex;
          align-items: center;
          overflow: hidden;
          padding: 25px 0;
        }
        .hero-background {
          position: absolute;
          inset: 0;
          background: linear-gradient(135deg, #f0fdfa 0%, #ccfbf1 100%);
        }
        .hero-overlay {
          position: absolute;
          inset: 0;
          background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><circle cx="20" cy="20" r="2" fill="rgba(6,182,212,0.05)"/><circle cx="80" cy="80" r="1.5" fill="rgba(6,182,212,0.03)"/></svg>')
            repeat;
        }

        .hero-content {
          position: relative;
          padding-right: 2rem;
        }
        .hero-badge {
          display: inline-block;
          padding: 8px 20px;
          background: rgba(6, 182, 212, 0.1);
          color: var(--primary-color);
          border-radius: 50px;
          font-size: 14px;
          font-weight: 600;
          margin-bottom: 24px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        .hero-title {
          font-size: clamp(2.5rem, 4vw, 4rem);
          font-weight: 800;
          color: var(--text-dark);
          margin-bottom: 12px;
          line-height: 1.1;
        }
        .hero-subtitle {
          font-size: 1.5rem;
          color: var(--primary-color);
          font-weight: 600;
          margin-bottom: 24px;
        }
        .hero-description {
          font-size: 1.2rem;
          color: var(--text-gray);
          margin-bottom: 40px;
          line-height: 1.6;
          max-width: 600px;
        }

        .hero-image-container {
          padding-left: 2rem;
        }
        .project-image {
          position: relative;
          width: 100%;
          height: 400px;
          border-radius: 20px;
          overflow: hidden;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
        }
        .hero-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        .image-placeholder {
          display: none;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 100%;
          background: linear-gradient(
            135deg,
            var(--primary-color),
            var(--primary-dark)
          );
          color: white;
          text-align: center;
          gap: 16px;
        }

        /* Overview Section */
        .overview-section {
          padding: 100px 0;
          background: var(--light-bg);
        }
        .project-image-container {
          position: relative;
        }
        .project-overview-image {
          width: 100%;
          height: 500px;
          object-fit: cover;
          border-radius: 20px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
        }
        .overview-content {
          padding-left: 48px;
        }
        .section-badge {
          display: inline-block;
          padding: 6px 16px;
          background: rgba(6, 182, 212, 0.1);
          color: var(--primary-color);
          border-radius: 50px;
          font-size: 12px;
          font-weight: 600;
          margin-bottom: 20px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        .section-title {
          font-size: clamp(2rem, 3vw, 2.5rem);
          font-weight: 700;
          color: var(--text-dark);
          margin-bottom: 24px;
          line-height: 1.2;
        }
        .section-description {
          font-size: 1.1rem;
          color: var(--text-gray);
          margin-bottom: 40px;
          line-height: 1.6;
        }

        .impact-areas {
          margin-top: 48px;
        }
        .subsection-title {
          font-size: 1.3rem;
          font-weight: 600;
          color: var(--text-dark);
          margin-bottom: 24px;
        }
        .impact-item {
          margin-bottom: 24px;
          padding-bottom: 24px;
          border-bottom: 1px solid var(--border-light);
        }
        .impact-item:last-child {
          border-bottom: none;
        }
        .impact-title {
          font-size: 1.1rem;
          font-weight: 600;
          color: var(--primary-color);
          margin-bottom: 8px;
        }
        .impact-description {
          color: var(--text-gray);
          margin: 0;
        }

        /* Pillars Section */
        .pillars-section {
          padding: 100px 0;
          background: white;
        }
        .section-header {
          margin-bottom: 80px;
          opacity: 0;
          transform: translateY(30px);
          transition: all 0.8s ease;
        }
        .section-header.animate-in {
          opacity: 1;
          transform: translateY(0);
        }
        .pillar-card {
          background: white;
          border-radius: 20px;
          padding: 40px;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
          transition: all 0.3s ease;
          height: 100%;
          border: 1px solid var(--border-light);
        }
        .pillar-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 16px 48px rgba(0, 0, 0, 0.12);
        }
        .pillar-icon {
          width: 80px;
          height: 80px;
          background: linear-gradient(
            135deg,
            var(--primary-color),
            var(--primary-dark)
          );
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          margin-bottom: 24px;
        }
        .pillar-title {
          font-size: 1.5rem;
          font-weight: 700;
          color: var(--text-dark);
          margin-bottom: 16px;
        }
        .pillar-description {
          color: var(--text-gray);
          margin-bottom: 24px;
          line-height: 1.6;
        }
        .pillar-highlights {
          list-style: none;
          padding: 0;
          margin: 0;
        }
        .pillar-highlights li {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 12px;
          color: var(--text-gray);
          font-size: 0.95rem;
        }
        .pillar-highlights li svg {
          color: var(--success-green);
          flex-shrink: 0;
        }

        /* Emergency Section */
        .emergency-section {
          padding: 80px 0;
          background: var(--light-bg);
        }
        .emergency-card {
          background: linear-gradient(135deg, var(--warning-red), #dc2626);
          border-radius: 20px;
          padding: 60px;
          color: white;
          text-align: center;
        }
        .emergency-content {
          display: flex;
          align-items: center;
          gap: 40px;
          max-width: 800px;
          margin: 0 auto;
        }
        .emergency-icon {
          background: rgba(255, 255, 255, 0.2);
          border-radius: 50%;
          padding: 24px;
          flex-shrink: 0;
        }
        .emergency-title {
          font-size: 2rem;
          font-weight: 700;
          margin-bottom: 16px;
        }
        .emergency-description {
          font-size: 1.1rem;
          opacity: 0.9;
          margin-bottom: 32px;
        }
        .emergency-actions {
          display: flex;
          gap: 16px;
          justify-content: center;
          flex-wrap: wrap;
        }

        /* Budget Section */
        .budget-section {
          padding: 80px 0;
          background: var(--light-bg);
        }
        .budget-card {
          background: white;
          border-radius: 20px;
          padding: 40px;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
          border: 1px solid var(--border-light);
        }
        .budget-title {
          font-size: clamp(1.6rem, 2.5vw, 2rem);
          font-weight: 700;
          margin-bottom: 6px;
          color: var(--text-dark);
        }
        .budget-subtitle {
          color: var(--text-gray);
          margin-bottom: 24px;
        }

        .budget-table thead th {
          border-bottom: 2px solid var(--border-light);
          color: var(--text-dark);
        }
        .budget-table tbody td,
        .budget-table tfoot th,
        .budget-table tfoot td {
          vertical-align: middle;
        }
        .budget-table tfoot th,
        .budget-table tfoot td {
          border-top: 2px solid var(--border-light);
          font-weight: 700;
        }

        .budget-bars {
          display: grid;
          grid-template-columns: 1fr;
          gap: 12px;
          margin-top: 20px;
        }
        .bar {
          display: grid;
          grid-template-columns: 240px 1fr 70px;
          gap: 12px;
          align-items: center;
        }
        .bar span {
          color: var(--text-dark);
          font-weight: 500;
        }
        .bar strong {
          text-align: right;
          color: var(--text-dark);
        }
        .bar-track {
          height: 10px;
          border-radius: 999px;
          background: #eef2f7;
          overflow: hidden;
          border: 1px solid var(--border-light);
        }
        .bar-fill {
          height: 100%;
          width: 0%;
          background: linear-gradient(
            135deg,
            var(--primary-color),
            var(--primary-dark)
          );
          border-radius: 999px;
          transition: width 0.8s ease;
        }

        /* Responsive */
        @media (max-width: 1200px) {
          .overview-content {
            padding-left: 24px;
          }
          .hero-content {
            padding-right: 1rem;
          }
          .hero-image-container {
            padding-left: 1rem;
          }
        }
        @media (max-width: 992px) {
          .overview-content {
            padding-left: 0;
            margin-top: 48px;
          }
          .hero-content {
            padding-right: 0;
            margin-bottom: 48px;
          }
          .hero-image-container {
            padding-left: 0;
          }
          .emergency-content {
            flex-direction: column;
            text-align: center;
          }
        }
        @media (max-width: 768px) {
          .hero-section {
            padding: 20px 0;
          }
          .pillar-card {
            padding: 32px 24px;
          }
          .cta-card,
          .emergency-card {
            padding: 60px 24px;
          }
          .btn {
            padding: 14px 28px;
            font-size: 0.95rem;
          }
          .bar {
            grid-template-columns: 1fr;
          }
          .bar strong {
            text-align: left;
          }
        }

        /* Animation Utilities */
        .animate-in {
          animation: slideInUp 0.8s ease-out forwards;
        }
        @keyframes slideInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default RHASMPProject;
